import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import { useAuth } from 'contexts/AuthContext';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
// Import theme context and icons for theme toggle
import { useTheme } from 'contexts/ThemeContext';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useTheme as useMuiTheme } from '@mui/material/styles';
// Import app name from config
import { APP_NAME } from 'config/AppConfig';

// Create a styled wrapper for the app name
const AppNameWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  '& a': {
    textDecoration: 'none',
    fontSize: '1.2rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#2196F3', // Solid color instead of gradient
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    display: 'inline-block',
    '&:hover': {
      transform: 'scale(1.03)',
    },
  }
}));

const TopBar: React.FC = () => {
  const { auth, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    logout();
  };

  const handleThemeToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent menu from closing
    toggleTheme();
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        width: 'calc(100% - 60px)', 
        ml: '60px',
        backgroundColor: muiTheme.palette.background.paper,
        color: muiTheme.palette.text.primary,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: '47px !important',
          height: '47px'
        }}
      >
        <AppNameWrapper>
          <Typography component={Link} to="/" variant="h6">
            {APP_NAME}
          </Typography>
        </AppNameWrapper>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {auth.isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Welcome, {auth.user?.name?.split(' ')[0] || 'User'}
              </Typography>
              <IconButton
                onClick={handleMenu}
                size="small"
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{ padding: 0 }}
              >
                <Avatar 
                  alt={auth.user?.name || 'User'} 
                  src={auth.user?.picture || undefined}
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'profile-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    minWidth: 200,
                    borderRadius: '8px',
                    mt: 1.5,
                    '& .MuiMenuItem-root': {
                      px: 2,
                      py: 1.5,
                      fontSize: '0.875rem',
                    }
                  }
                }}
              >
                <MenuItem 
                  component={Link} 
                  to="/profile" 
                  onClick={handleClose}
                  sx={{
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' }
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="My Profile" />
                </MenuItem>
                
                {/* Simplified Theme Toggle Menu Item */}
                <MenuItem
                  onClick={handleThemeToggle}
                  sx={{
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' }
                  }}
                >
                  <ListItemIcon>
                    {mode === 'light' ? (
                      <DarkModeIcon fontSize="small" color="primary" />
                    ) : (
                      <LightModeIcon fontSize="small" color="primary" />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={mode === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"} 
                  />
                </MenuItem>
                
                <Divider sx={{ my: 0.5 }} />
                <MenuItem 
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' }
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Log out" />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button 
              color="inherit" 
              component={Link} 
              to="/login"
            >
              Login
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;